<template>
    <div class="bar-chart" ref="chartContainer"></div>
</template>

<script>
import Vue from "vue";
import * as echarts from "echarts/core";
import { BarChart } from "echarts/charts";
import { 
    GridComponent, 
    TooltipComponent, 
    LegendComponent, 
    TitleComponent,
    MarkLineComponent 
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import _ from 'lodash';

echarts.use([
    BarChart, 
    GridComponent, 
    TooltipComponent, 
    LegendComponent, 
    TitleComponent, 
    MarkLineComponent,
    CanvasRenderer
]);

export default Vue.extend({
    name: "BarChart",
    props: {
        data: {
            type: Array,
            required: true
        },
        settings: {
            type: Object,
            required: true
        },
        title: {
            type: String,
            default: ""
        },
        fields: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            chart: null,
            resizeObserver: null
        };
    },
    computed: {
        chartOptions() {
            // X ekseni field'ını bul
            const xAxisField = this.fields.find(f => f._id === _.get(this.settings, 'xAxis.field'));
            if (!xAxisField) return null;

            // Y ekseni field'ını bul (eğer calculationType count değilse)
            const yAxisField = _.get(this.settings, 'yAxis.calculationType') !== 'count' 
                ? this.fields.find(f => f._id === _.get(this.settings, 'yAxis.field'))
                : null;

            // Veriyi hazırla
            const xAxisData = this.data.map(item => {
                const value = item[xAxisField.name];
                const customization = _.get(this.settings, `customization.labelCustomization.${value}`);
                return customization?.label || value;
            });

            // Renk ayarlarını al
            const defaultCustomization = _.get(this.settings, 'customization.labelCustomization.default');
            const defaultColor = defaultCustomization?.color || '#1890ff';
            const defaultLabel = defaultCustomization?.label || (yAxisField 
                ? `${yAxisField.name} (${_.get(this.settings, 'yAxis.calculationType')})`
                : 'Kayıt Sayısı');

            // Y ekseni etiketi
            const yAxisLabel = yAxisField 
                ? `${yAxisField.name} (${_.get(this.settings, 'yAxis.calculationType')})`
                : `Kayıt Sayısı`;

            return {
                title: {
                    text: this.title,
                    left: 'center'
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    },
                    formatter: (params) => {
                        const param = params[0];
                        return `${param.name}<br/>${yAxisLabel}: ${param.value}`;
                    }
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '3%',
                    containLabel: true
                },
                xAxis: {
                    type: 'category',
                    data: xAxisData,
                    name: xAxisField.name,
                    axisTick: {
                        alignWithLabel: true
                    }
                },
                yAxis: {
                    type: 'value',
                    name: yAxisLabel
                },
                series: [{
                    name: defaultLabel,
                    type: 'bar',
                    data: this.data.map(item => item.value),
                    itemStyle: {
                        color: defaultColor
                    },
                    label: {
                        show: true,
                        position: 'top'
                    },
                    markLine: {
                        silent: true,
                        symbol: ['none', 'none'],
                        data: this.getBenchmarkLines()
                    }
                }],
            };
        }
    },
    mounted() {
        this.initChart();
        this.initResizeObserver();
    },
    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    },
    methods: {
        getBenchmarkLines() {
            const benchmarkLines = _.get(this.settings, 'benchmarkLines', []);
            if (!benchmarkLines || !benchmarkLines.length) return [];

            return benchmarkLines.map(line => ({
                name: line.label,
                lineStyle: {
                    color: line.color,
                    type: 'dashed',
                    width: 2
                },
                label: {
                    formatter: `${line.label}${line.text ? ': ' + line.text : ''}`,
                    position: 'middle',
                    show: true,
                    color: line.color,
                    fontSize: 12,
                    backgroundColor: '#fff',
                    padding: [4, 8]
                },
                [line.axis === 'x' ? 'xAxis' : 'yAxis']: line.value
            }));
        },
        initChart() {
            if (!this.$refs.chartContainer) return;
            
            this.chart = echarts.init(this.$refs.chartContainer);
            this.updateChart();
        },
        initResizeObserver() {
            this.resizeObserver = new ResizeObserver(_.debounce(() => {
                if (this.chart) {
                    this.chart.resize();
                }
            }, 100));

            if (this.$refs.chartContainer) {
                this.resizeObserver.observe(this.$refs.chartContainer);
            }
        },
        updateChart() {
            if (!this.chart || !this.chartOptions) return;
            this.chart.setOption(this.chartOptions, true);
            this.chart.resize();
        }
    },
    watch: {
        data: {
            handler() {
                this.$nextTick(() => {
                    this.updateChart();
                });
            },
            deep: true
        },
        settings: {
            handler() {
                this.$nextTick(() => {
                    this.updateChart();
                });
            },
            deep: true
        }
    }
});
</script>

<style scoped lang="scss">
.bar-chart {
    width: 100%;
    height: 100%;
    min-height: 200px;
}
</style> 